/**
 * Protocol REST API endpoints.
 *
 * The gateway serves `/api/v1/protocols/**` itself: each route calls
 * tricorder-protocol's MCP tool of the same operation with the signed-in
 * user's delegation token (the platform ledger's OPEN #28). The comments and
 * assignments on a version are tricorder-studio's (`./collab.ts`). The wire
 * shapes below are the gateway's:
 *
 *   - A version's lifecycle is `status` (draft, in_review, published,
 *     archived), derived server-side from its `period`. There is no
 *     `published_at`, `links_count` or `can_edit`: a write the caller may not
 *     make answers 403.
 *   - No write is conditional and no response carries an `ETag`. A draft's
 *     `PUT` is a full replace; a repeated lifecycle command answers 409 naming
 *     the status, because the move it asks for no longer exists.
 *   - Errors are RFC 9457 problem bodies; `api` reads their `detail`.
 *
 * A protocol run is stateless on the server: `:step` evaluates one move and
 * answers the step message the caller appends to the run's own
 * tricorder-session session, and `:fold` turns that log back into the run's
 * state. A version's documents are part of its spec
 * (`source.sourceDocumentId`, the primary source, and `relatedArtifacts`),
 * written with the draft's PUT. The Studio assistant is a session with the
 * `protocol-assistant` agent, not a route here.
 */

import { z } from 'zod'

import { api } from '../client'
import { isRecord } from '../../lib/isRecord'
import type { Page } from '../types'
import type { Period } from '../temporal'
import { ProtocolStepPayloadSchema } from '../schemas_session'
import type { ProtocolObservation } from '../types_session'
import { parseResponse } from '../validation'
import { searchDocuments } from './documents'
import { randomUUID } from '../../lib/uuid'
// ApiError is thrown by `api.*` on non-2xx responses — re-export for callers
export { ApiError } from '../client'

// ── Wire types ────────────────────────────────────────────────────────────────

/** A version's lifecycle, derived server-side from its period. */
export type ProtocolVersionStatus = 'draft' | 'in_review' | 'published' | 'archived'

/**
 * One version without its spec: a row of `GET /protocols` (the version in
 * force, or else the newest, per protocol) and of `GET /protocols/{id}/versions`
 * (every version, newest first).
 */
export interface ProtocolSummary {
  id: string
  /** "v<n>". */
  version: string
  status: ProtocolVersionStatus
  /** Null while a draft; open while published; closed once archived. */
  period: Period | null
  title: string
  description: string | null
  /** The owning institution's slug; null for a global protocol. */
  institution: string | null
  skill_ids: string[]
  created_by: string
  created_at: string
  updated_at: string
  content_hash: string
}

/**
 * One version with its spec, as `GET`, `POST` and `PUT` on a version answer.
 * `spec` is the `tricorder.protocol.v1.Protocol` message as protojson, the
 * authoring shape the Studio edits.
 */
export interface ProtocolVersion extends ProtocolSummary {
  /** True when the version is its owner's alone. */
  private: boolean
  spec: unknown
}

/** The filters `GET /protocols` takes. Free text (`q`) answers 501 upstream. */
export interface ListProtocolsParams {
  status?: ProtocolVersionStatus
  /**
   * Filter on the row's own spec: `true` selects protocols whose graph has a
   * node that runs another component (a curriculum), `false` those that
   * reference none. Omitted, nothing is filtered.
   */
  composesComponents?: boolean
  skillIds?: string[]
  institution?: string
}

/** A ranked candidate document for a version, from the document search. */
export interface SuggestedDocument {
  document_id: string
  title: string
  score: number
}

// ── Reads ─────────────────────────────────────────────────────────────────────

/**
 * listProtocols reads every row of `GET /protocols` the filters select, one
 * page after another, following `Link: rel="next"` to the last. One row per
 * protocol: the version in force, or else the newest.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function listProtocols(params: ListProtocolsParams = {}): Promise<ProtocolSummary[]> {
  const query = new URLSearchParams()
  if (params.status) query.set('status', params.status)
  if (params.composesComponents !== undefined) {
    query.set('composes_components', String(params.composesComponents))
  }
  if (params.skillIds && params.skillIds.length > 0) query.set('skill_ids', params.skillIds.join(','))
  if (params.institution) query.set('institution', params.institution)

  const qs = query.toString()
  return api.getAllItems<Page<ProtocolSummary>>(`/protocols${qs ? `?${qs}` : ''}`)
}

/**
 * fetchProtocolVersions loads a protocol's versions, newest first
 * (`GET /protocols/{id}/versions`, 404 when it has none).
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function fetchProtocolVersions(id: string): Promise<ProtocolSummary[]> {
  return api.getAllItems<Page<ProtocolSummary>>(`/protocols/${id}/versions`)
}

/**
 * fetchProtocolVersion reads one version and its spec
 * (`GET /protocols/{id}/versions/{v}`). Drafts included, for a caller who may
 * view them.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function fetchProtocolVersion(id: string, version: string): Promise<ProtocolVersion> {
  return api.get<ProtocolVersion>(`/protocols/${id}/versions/${version}`)
}

// ── Suggest documents ─────────────────────────────────────────────────────────

/**
 * suggestQueryOf builds the search query that finds a version's source
 * documents. It says four things, in decreasing specificity: the title (where a
 * protocol's identity is written down, e.g. the exact figure of the exact
 * guideline), the description, the entry node's title in the spec's default
 * locale (the clinical action), and the source citation (often the document's
 * name). A repeated string is said once, because a repeated term skews the
 * embedding toward it. With nothing authored, the id is all the identity there
 * is.
 *
 * Ported from the gateway's deleted `buildSuggestQuery`
 * (api-service-go/protocol_fhir_handlers.go), which answered the deleted
 * `POST …/suggest-documents`.
 */
export function suggestQueryOf(version: ProtocolVersion): string {
  const parts: string[] = []
  const add = (s: string) => {
    const trimmed = s.trim()
    if (trimmed !== '' && !parts.includes(trimmed)) parts.push(trimmed)
  }

  add(version.title)
  add(version.description ?? '')

  const spec = isRecord(version.spec) ? version.spec : {}
  const nodes = Array.isArray(spec.nodes) ? spec.nodes : []
  const entry = nodes.find((n) => isRecord(n) && typeof n.id === 'string' && n.id !== '' && n.id === spec.entryNodeId)
  const content =
    isRecord(entry) && isRecord(entry.locales) && typeof spec.defaultLocale === 'string'
      ? entry.locales[spec.defaultLocale]
      : undefined
  if (isRecord(content) && typeof content.title === 'string') add(content.title)
  if (isRecord(spec.source) && typeof spec.source.citation === 'string') add(spec.source.citation)

  return parts.length > 0 ? parts.join(' ') : version.id
}

/**
 * suggestDocuments searches the document collection (`GET /documents?q=`) with
 * the query the version itself describes, one row per matching document, best
 * first.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function suggestDocuments(version: ProtocolVersion): Promise<SuggestedDocument[]> {
  const found = await searchDocuments(suggestQueryOf(version))
  return found.results.map((r) => ({ document_id: r.document_id, title: r.title, score: r.score }))
}

// ── Writes ────────────────────────────────────────────────────────────────────

/**
 * draftOf is the full draft body a version write sends: `base`'s own title,
 * description, institution, skills and privacy, with `spec` as the content.
 * tricorder-protocol inherits nothing between versions and a PUT clears every
 * field it is not sent, so every field travels. The spec carries its own state
 * block, so no `state_rules` is sent.
 */
function draftOf(base: ProtocolVersion, spec: unknown) {
  return {
    title: base.title,
    description: base.description,
    institution: base.institution,
    skill_ids: base.skill_ids,
    private: base.private,
    spec,
  }
}

/**
 * createProtocol creates a protocol and its first draft from a spec
 * (`POST /protocols`, body `{id, title, spec}`). The title is the protocol's
 * name as the author wrote it (the Studio's header, which also derives the
 * id); the server writes the id over the spec's.
 *
 * Throws `ApiError` on HTTP failure (409 when the id exists).
 */
export async function createProtocol(id: string, title: string, spec: unknown): Promise<ProtocolVersion> {
  return api.post<ProtocolVersion>('/protocols', { id, title, spec }, { headers: { 'Idempotency-Key': randomUUID() } })
}

/**
 * createProtocolVersion adds the next draft of `base`'s protocol
 * (`POST /protocols/{id}/versions`), continuing `base` with `spec`: the draft
 * carries `base`'s title, description, institution, skills and privacy.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function createProtocolVersion(base: ProtocolVersion, spec: unknown): Promise<ProtocolVersion> {
  return api.post<ProtocolVersion>(`/protocols/${base.id}/versions`, draftOf(base, spec), {
    headers: { 'Idempotency-Key': randomUUID() },
  })
}

/**
 * updateProtocolVersion replaces a draft (`PUT /protocols/{id}/versions/{v}`)
 * with `base`'s fields and a new spec. `base` is the version as last read or
 * written. The write is unconditional: of two saves of one draft, the later
 * one stands. A version that is not a draft answers 409 naming its status.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function updateProtocolVersion(base: ProtocolVersion, spec: unknown): Promise<ProtocolVersion> {
  return api.put<ProtocolVersion>(`/protocols/${base.id}/versions/${base.version}`, draftOf(base, spec))
}

/**
 * submitProtocolVersion hands a draft to approval (`:submit`). No approval
 * process is connected yet, so the draft is published at once and the
 * version in force before it is archived. A version that is not a draft
 * answers 409 naming its status.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function submitProtocolVersion(id: string, version: string): Promise<ProtocolVersion> {
  return api.post<ProtocolVersion>(`/protocols/${id}/versions/${version}:submit`)
}

/**
 * archiveProtocolVersion takes a published version out of force with no
 * successor (`:archive`). A version that is not published answers 409 naming
 * its status.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function archiveProtocolVersion(id: string, version: string): Promise<ProtocolVersion> {
  return api.post<ProtocolVersion>(`/protocols/${id}/versions/${version}:archive`)
}

/**
 * deleteProtocolVersion deletes ONE version of a protocol, whatever its
 * status.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function deleteProtocolVersion(id: string, version: string): Promise<void> {
  await api.delete<void>(`/protocols/${id}/versions/${version}`)
}

/**
 * deleteProtocol deletes a WHOLE protocol — every version of the id.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function deleteProtocol(id: string): Promise<void> {
  await api.delete<void>(`/protocols/${id}`)
}

// ── Runs ──────────────────────────────────────────────────────────────────────

const RunStatusSchema = z.enum(['in_progress', 'completed'])
const RejectedStepReasonSchema = z.enum(['forged', 'another_run', 'not_a_continuation', 'before_start'])

/** Where a run stands: on a node, or ended on a terminal one. */
export type ProtocolRunStatus = z.infer<typeof RunStatusSchema>

/** Why `:fold` left a step message out of the chain. */
export type RejectedStepReason = z.infer<typeof RejectedStepReasonSchema>

/** The action taken on the node the run stands on. */
export interface ProtocolRunAction {
  node_id: string
  action_id: string
  /** Keyed by each input's payload key, when the action captures a value. */
  observation?: ProtocolObservation
}

/** `:step`'s body. No `state_token` and no `action` starts the run. */
export interface StepProtocolRequest {
  /** The patient the run is about; absent for a run about nobody. */
  patient_id?: string
  state_token?: string
  action?: ProtocolRunAction
}

/** `:fold`'s body: the step messages' tokens, in log order. */
export interface FoldProtocolStepsRequest {
  patient_id?: string
  state_tokens: string[]
}

const StepProtocolResponseSchema = z.object({
  state_token: z.string().min(1),
  state: z.unknown(),
  // The node the run stands on, in the shape of the spec's `nodes[i]`.
  current_node: z.unknown(),
  status: RunStatusSchema,
  step: ProtocolStepPayloadSchema,
})

const FoldProtocolStepsResponseSchema = z.object({
  state_token: z.string().min(1),
  state: z.unknown(),
  current_node: z.unknown(),
  status: RunStatusSchema,
  rejected: z.array(
    z.object({
      index: z.number().int(),
      reason: RejectedStepReasonSchema,
    }),
  ),
})

/** What `:step` answers: the new state, and in `step` the step message to
 *  append to the run's log verbatim. */
export type StepProtocolResponse = z.infer<typeof StepProtocolResponseSchema>

/** What `:fold` answers: the state the valid chain ends on, and every token
 *  it left out, by index and reason. */
export type FoldProtocolStepsResponse = z.infer<typeof FoldProtocolStepsResponseSchema>

/**
 * stepProtocol takes one step of a run (`:step`). The server keeps no run:
 * the caller sends the last state token and appends the answer's `step` to
 * the run's log, and only after the step was accepted. A stale state or a
 * repeated click answers 409, because the move no longer exists.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function stepProtocol(
  id: string,
  version: string,
  request: StepProtocolRequest,
): Promise<StepProtocolResponse> {
  const path = `/protocols/${id}/versions/${version}:step`
  return parseResponse(StepProtocolResponseSchema, await api.post<unknown>(path, request), `POST ${path}`)
}

/**
 * foldProtocolSteps turns a run's log back into its state (`:fold`). A log
 * with no valid start answers 409.
 *
 * Throws `ApiError` on HTTP failure.
 */
export async function foldProtocolSteps(
  id: string,
  version: string,
  request: FoldProtocolStepsRequest,
): Promise<FoldProtocolStepsResponse> {
  const path = `/protocols/${id}/versions/${version}:fold`
  return parseResponse(FoldProtocolStepsResponseSchema, await api.post<unknown>(path, request), `POST ${path}`)
}
